import { r as registerInstance, c as createEvent, h, H as Host, g as getElement } from './index-8f7fb048.js';
import { c as classNames } from './classnames-ba9ba5b9.js';

const marketCodeButtonCss = ":host,::slotted(*),*{box-sizing:border-box}:host([disabled]){cursor:not-allowed !important;}:host([disabled]) *,:host([disabled]) ::slotted(*){cursor:not-allowed !important;pointer-events:none !important;}:host{display:flex;justify-content:space-between;align-items:center;width:-moz-min-content;width:min-content;min-width:var(--code-button-width, 320px);min-height:var(--code-button-height, 48px);margin:var(--code-button-margin, 12px);padding:var(--code-button-padding-vertical, 12px)\n    var(--code-button-padding-right, 16px)\n    var(--code-button-padding-vertical, 12px)\n    var(--code-button-zero-value, 0);border-radius:var(--code-button-border-radius, 6px);background-color:var(--code-button-background-color, var(--core-fill-40-color));font-weight:var(--code-button-code-font-weight, var(--core-type-bold-weight));font-size:var(--code-button-code-font-size, var(--core-type-heading-20-size));font-family:var(--code-button-font-family, var(--core-type-font-family));font-feature-settings:'tnum'}:host(:not([disabled])) button:hover,:host ::slotted(button:hover),:host ::slotted(a:hover){opacity:var(--code-button-action-interaction-opacity, 0.6)}:host(:not([disabled])) button:active,:host ::slotted(button:active),:host ::slotted(a:active){opacity:var(--code-button-action-interaction-opacity, 0.6)}:host([disabled]){color:var(--code-button-disabled-text-color, var(--core-text-30-color));}:host([disabled]) ::slotted(button),:host ::slotted(a){color:var(--code-button-disabled-text-color, var(--core-text-30-color));}:host .code-container{display:flex;flex-grow:2;justify-content:space-between;min-width:var(--code-button-code-container-min-width, 269px)}:host .code-char{flex-basis:100%;text-align:center}:host .actions-container{display:flex;justify-content:center;align-items:center}:host .actions-container ::slotted([slot='actions']:not(:last-child)){margin-right:var(--code-button-extra-actions-margin-right, 25px)}:host .actions-container ::slotted([slot='actions']:not(:last-child))::after{content:'';display:inline-block;width:var(--code-button-button-separator-width, 1px);height:var(--code-button-button-separator-height, 8px);margin-right:var(--code-button-separator-margin-right, -13px);margin-left:var(--code-button-button-content-spacing, 12px);background-color:var(--code-button-button-separator-color, var(--core-fill-10-color));opacity:var(--code-button-button-separator-opacity, 0.3);pointer-events:none}:host button,:host ::slotted(button),:host ::slotted(a){margin:var(--code-button-zero-value, 0);padding:var(--code-button-zero-value, 0);border:none;background-color:transparent;color:var(--code-button-button-font-color, var(--core-blue-text-color));font-weight:var(--code-button-button-font-weight, var(--core-type-semibold-weight));font-size:var(--code-button-button-font-size, var(--core-type-paragraph-20-size));font-family:inherit;line-height:var(--code-button-button-line-height, var(--core-type-paragraph-20-leading));text-decoration:none;cursor:pointer}@media only screen and (max-width: 320px){:host{flex-wrap:wrap;width:100%;min-width:var(--code-button-zero-value, 0)}:host .code-container{min-width:var(--code-button-zero-value, 0)}}";

/**
 * When code is split up, represents how many characters are in each group.
 */
const CODE_GROUP_LEN = 4;
const MarketCodeButton = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.marketCodeCopied = createEvent(this, "marketCodeCopied", 7);
    /**
     * A string holding the code inputted by the user through a slot.
     */
    this.code = '';
    /**
     * An array holding spans containing each character in the code, with some blanks.
     * Used for styling and spacing.
     */
    this.codeChars = [];
    this.disabled = false;
    this.focused = false;
  }
  /**
   * Component Lifecycle Event:
   * Grab the code and its len from inputted code slot.
   * Set relevant props and insert the Copy button programmatically so that it appears in the light DOM.
   */
  componentWillLoad() {
    // Set code and codeGroups
    const slottedCodeTag = this.el.querySelector('[slot=code]');
    const code = slottedCodeTag.textContent;
    // Remove whitespace from slotted code
    this.code = code.replace(/\s/g, '');
    this.initCodeChars(this.code);
    // Programmatically insert default Copy button into light DOM
    this.copyButton = document.createElement('button');
    Object.assign(this.copyButton, {
      slot: 'actions',
      type: 'button',
      tabIndex: this.disabled ? -1 : 0,
      onclick: () => this.copyToClipboard(),
    });
    // NOTE: The following code is brittle! Relies on the fact that the consumer
    //       slotted in elements in order of 'code' -> 'copy-text' -> 'actions'
    // If consumer slotted in alt copy-text, place copyButton after code & copy-text
    if (this.el.querySelector('[slot=copy-text]')) {
      // Set text of the Copy button to slotted copy-text
      const slottedCopyText = this.el.querySelector('[slot=copy-text]');
      const copyText = slottedCopyText.textContent;
      this.copyButton.innerHTML = copyText;
      slottedCopyText.insertAdjacentElement('afterend', this.copyButton);
    }
    else {
      this.copyButton.innerHTML = `Copy`;
      slottedCodeTag.insertAdjacentElement('afterend', this.copyButton);
    }
  }
  /**
   * Inits this.codeChars to an array of <span> tags containing each character in the code string.
   * Additionally contains empty strings at certain indices based on codeLen.
   * By default, blanks are only placed at the front & back of the array.
   *    ['', 'x', 'x', 'x', 'x', '']
   * If codeLen is divisible by 4, put blanks every 4 spots too.
   *    ['', 'x', 'x', 'x', 'x', '', 'x', 'x', 'x', 'x', '']
   * The exception to this is if codeLen===4, in which case we use the default.
   * This codeChars arr is used in render() to space and style characters appropriately.
   * Called only once on componentWillLoad() as to not cause re-renders
   */
  initCodeChars(code) {
    const codeLen = code.length;
    const BLANK_CODE_SPAN = h("span", { class: "code-char" });
    // Place blanks every 4 chars
    if (codeLen !== CODE_GROUP_LEN && codeLen % CODE_GROUP_LEN === 0) {
      for (let i = 0; i < codeLen; i++) {
        if (i % CODE_GROUP_LEN === 0)
          this.codeChars.push(BLANK_CODE_SPAN);
        this.codeChars.push(h("span", { class: "code-char" }, code[i]));
      }
    }
    // Only place blank at the front & back
    else {
      this.codeChars.push(BLANK_CODE_SPAN);
      for (let i = 0; i < codeLen; i++) {
        this.codeChars.push(h("span", { class: "code-char" }, code[i]));
      }
    }
    this.codeChars.push(BLANK_CODE_SPAN);
  }
  /**
   * When user presses the 'Copy' button, copy code to clipboard & emit an event.
   */
  copyToClipboard() {
    navigator.clipboard.writeText(this.code);
    this.marketCodeCopied.emit();
  }
  render() {
    return (h(Host, { class: classNames({ 'focus-visible': this.focused }), tabIndex: this.disabled ? null : 0, "aria-label": this.code }, h("span", { class: "code-container" }, this.codeChars), h("span", { class: "actions-container" }, h("slot", { name: "actions" }))));
  }
  get el() { return getElement(this); }
};
MarketCodeButton.style = marketCodeButtonCss;

export { MarketCodeButton as market_code_button };
